import { ENTITY } from '@/enums';
import { productParams, useFetchProducts } from '@/hooks';
import { OrderEntity } from '@/pages/privatePages/order/types';
import { ProductEntity } from '@/pages/privatePages/product/types';
import { CustomResponse } from '@/services/api-services/type';
import { errorNotification, exportExcel, renderCondition } from '@/utils';
import {
  CustomButton,
  CustomColumnType,
  CustomScrollbar,
} from '@amili-v3/amili-v3-design';
import { useQuery } from '@tanstack/react-query';
import { Checkbox, Progress } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { AxiosResponse } from 'axios';
import { isString } from 'lodash';
import _get from 'lodash/get';
import { GetRowKey } from 'rc-table/lib/interface';
import {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CustomModalExportStyle, ExportProgressWrapper } from './style';

const CheckboxGroup = Checkbox.Group;

const streamModules = [
  ENTITY.KITS,
  ENTITY.ORDER,
  ENTITY.EXTRACTION,
  ENTITY.SEQUENCES,
  ENTITY.PROJECTS,
  ENTITY.CUSTOMER,
];
export const streamQuantityEventName = 'streamNumberOfRecords';

type ColumnsDataType = {
  allColumns: CustomColumnType<any>[];
  columnTitles: string[];
};
type ExportByColumnProps = {
  isOpen: boolean;
  total?: number;
  closeModal: () => void;
  columns?: CustomColumnType<any>[];
  isSelectAllRow?: boolean;
  unCheckedListArr?: string[];
  checkedListArray: string[];
  handleFetch: (
    props: Record<string, any>,
    actionType?: 'exports',
  ) => Promise<AxiosResponse<any, any> | string | CustomResponse<any>>;
  params?: any;
  entity: string;
  setCheckedListOfAllPage?: Dispatch<SetStateAction<Record<number, string[]>>>;
  setCheckedRecordByPage?: React.Dispatch<
    React.SetStateAction<Record<number, any[]>>
  >;
  rowKey: string | GetRowKey<any>;
  isSortColumns?: boolean;
  transformData?: (
    orders: OrderEntity[],
    products: ProductEntity[],
  ) => OrderEntity[];
};

const ExportByColumnModal: FC<ExportByColumnProps> = props => {
  const {
    isOpen,
    closeModal: toggleModal,
    isSortColumns,
    columns: columnsTable = [],
    isSelectAllRow,
    unCheckedListArr,
    handleFetch,
    params = [],
    entity,
    setCheckedListOfAllPage,
    setCheckedRecordByPage,
    rowKey,
    total,
    transformData,
  } = props;
  const { t } = useTranslation();

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  // const [isExportActionReadyToGo, setExportActionReadyToGo] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [checkAll, setCheckAll] = useState(true);
  const [percentProgress, setPercentProgress] = useState(0);
  const [allowExport, setAllowExport] = useState(false);

  const { products } = useFetchProducts(productParams, {
    enabled: !!transformData && isOpen,
    staleTime: Infinity,
  });

  const columnsData = useMemo<ColumnsDataType>(() => {
    if (!columnsTable) return { allColumns: [], columnTitles: [] };

    const sortedColumns = isSortColumns
      ? [...columnsTable].sort(
          (a, b) => Number(a?.exportKey) - Number(b?.exportKey),
        )
      : columnsTable;
    const columnTitles = sortedColumns.reduce<string[]>((res, column) => {
      if (
        column.key === 'actions' ||
        column.className === 'actions' ||
        column.isClearOnExport
      ) {
        return res;
      }
      const title = isString(column.title)
        ? column.title
        : (column?.exportTitle ?? '');

      res.push(title);
      return res;
    }, []);

    return { allColumns: sortedColumns, columnTitles };
  }, [columnsTable, isSortColumns]);

  useEffect(() => {
    setSelectedValues(columnsData.columnTitles);
  }, [columnsData.columnTitles]);

  const handleChangeSelectCol = useCallback(
    (list: any[]) => {
      setSelectedValues(list);
      setIndeterminate(
        !!list.length && list.length < columnsData.columnTitles.length,
      );
      setCheckAll(list.length === columnsData.columnTitles.length);
    },
    [columnsData.columnTitles.length],
  );

  const handleClose = useCallback(() => {
    setSelectedValues(columnsData.columnTitles);
    setIndeterminate(false);
    setCheckAll(true);
    setPercentProgress(0);
    setAllowExport(false);
    toggleModal();
  }, [columnsData.columnTitles, toggleModal]);

  const {
    data: allExportData,
    isSuccess: isSuccessQueryExport,
    isError: isErrorQueryExport,
    error: errorExport,
    refetch: refetchExport,
    isLoading: isLoadingExport,
    isFetching: isFetchingExport,
  } = useQuery({
    queryKey: [{ scope: 'stream', entity, params }],
    queryFn: () => {
      const newParams = { ...params, excludeIds: unCheckedListArr };
      return handleFetch(newParams);
    },
    enabled: false,
  });
  const isLoading = isFetchingExport || isLoadingExport;
  //Process when query is success
  useEffect(() => {
    if (!isLoading && isSuccessQueryExport && allowExport) {
      let rawExportData: Array<any> = [];

      if (typeof allExportData !== 'string') {
        rawExportData = allExportData?.data?.data || allExportData?.data || [];
      } else if (streamModules.includes(entity as ENTITY)) {
        const streamData = allExportData;

        try {
          rawExportData = JSON.parse(
            `[${streamData.slice(0, streamData.length - 2)}]`,
          );
        } catch (e) {
          console.error(e);
        }
      }

      let filteredExportData;
      if (isSelectAllRow && !!rawExportData.length) {
        filteredExportData = rawExportData.reduce(
          (acc: Record<string, any>[], rawExportItem) => {
            const exportItem = rawExportItem.data || rawExportItem;
            const key =
              typeof rowKey === 'string' ? exportItem : rowKey(exportItem);
            if (!unCheckedListArr?.includes(key)) {
              acc.push(exportItem);
            }
            return acc;
          },
          [],
        );
      } else {
        filteredExportData = rawExportData;
      }
      if (transformData && !!products.length) {
        filteredExportData = transformData(filteredExportData, products);
      }

      const selectedColumns = columnsData.allColumns.filter(col =>
        selectedValues.includes(col?.title as string),
      );
      const exportRows = filteredExportData.reduce(
        (result: any[], item: any) => {
          const row: any = {};

          selectedColumns.forEach(column => {
            const { render, renderColumnExcel, dataIndex } = column;

            let cellValue: string | number = dataIndex
              ? _get(item, dataIndex, '')
              : '';
            if (dataIndex) {
              if (renderColumnExcel) {
                cellValue = renderColumnExcel(_get(item, dataIndex), item, 0);
              } else {
                if (render) {
                  const renderResult = renderCondition(
                    render(_get(item, dataIndex), item, 0),
                  );
                  if (
                    typeof renderResult === 'string' ||
                    typeof renderResult === 'number'
                  ) {
                    cellValue = renderResult;
                  }
                }
              }
            }

            row[column?.title as string] = cellValue;
          });

          result.push(row);
          return result;
        },
        [],
      );

      const now = new Date();

      let exportEntityName = entity;
      if (entity === ENTITY.KITS) {
        const firstChannelName = rawExportData[0]?.data?.channel?.name ?? '';
        const haveManyChannels = rawExportData.some(
          item => item?.data?.channel?.name !== firstChannelName,
        );
        exportEntityName = haveManyChannels
          ? entity
          : `${firstChannelName.toUpperCase()}_${entity}`;
      }
      const fileName = `${exportEntityName}_${now.getFullYear()}${
        now.getMonth() + 1
      }${now.getDate()}_${now.getHours()}${now.getMinutes()}${now.getSeconds()}`;

      exportExcel(exportRows, fileName.toUpperCase());
      setCheckedListOfAllPage?.({});
      setCheckedRecordByPage?.({});
      handleClose();
    }
  }, [
    allExportData,
    columnsData.allColumns,
    entity,
    handleClose,
    isFetchingExport,
    isLoadingExport,
    isSelectAllRow,
    isSuccessQueryExport,
    products,
    rowKey,
    selectedValues,
    setCheckedListOfAllPage,
    setCheckedRecordByPage,
    transformData,
    unCheckedListArr,
    isLoading,
    allowExport,
    isOpen,
  ]);

  //Process when query is error
  useEffect(() => {
    if (isErrorQueryExport && errorExport) {
      errorNotification({ error: errorExport as Error });
    }
  }, [errorExport, isErrorQueryExport]);

  const handleExport = useCallback(async () => {
    await refetchExport();
    setAllowExport(true);
  }, [refetchExport]);

  const handleCheckAllChange = (e: CheckboxChangeEvent) => {
    setSelectedValues(e.target.checked ? columnsData.columnTitles : []);
    setIndeterminate(false);
    setCheckAll(e.target.checked);
  };

  // Handle quantity of kit signal
  useEffect(() => {
    const handler = (event: Event) => {
      const e = event as CustomEvent;
      setPercentProgress(e.detail?.percentProgress ?? 0);
    };

    window.addEventListener(streamQuantityEventName, handler);

    return () => window.removeEventListener(streamQuantityEventName, handler);
  }, []);

  return (
    <CustomModalExportStyle
      destroyOnClose
      title={t('common.selectColumnsToPrint')}
      open={isOpen}
      onCancel={handleClose}
      footer={
        <CustomButton
          loading={isLoading}
          onClick={handleExport}
          disabled={!selectedValues.length}
          aria-label="Export btn"
          size="large"
        >
          {t('common.proceed')}
        </CustomButton>
      }
      centered
      width={470}
    >
      {isLoading && total && (
        <ExportProgressWrapper>
          <Progress percent={percentProgress} />
        </ExportProgressWrapper>
      )}

      {(!isLoading || !total) && (
        <CustomScrollbar style={{ height: 'min(600px, 50vh)' }}>
          <Checkbox
            indeterminate={indeterminate}
            onChange={handleCheckAllChange}
            checked={checkAll}
          >
            {t('common.allColumns')}
          </Checkbox>
          <CheckboxGroup
            style={{ display: 'flex', flexDirection: 'column' }}
            options={
              entity === ENTITY.REPORTS
                ? columnsData.columnTitles.slice(0, 6)
                : columnsData.columnTitles
            }
            value={selectedValues}
            onChange={handleChangeSelectCol}
          />
        </CustomScrollbar>
      )}
    </CustomModalExportStyle>
  );
};

export default memo(ExportByColumnModal);
